import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Clock, Filter, ChevronRight, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import Layout from "@/components/layout/Layout";
import axios from "axios";
import { API } from "@/App";

const CATEGORIES = [
  { id: "all", name: "Todos", icon: "📅", color: "#6B7280" },
  { id: "ferias", name: "Ferias", icon: "🎪", color: "#F59E0B" },
  { id: "conciertos", name: "Conciertos", icon: "🎵", color: "#8B5CF6" },
  { id: "deportes", name: "Deportes", icon: "⚽", color: "#10B981" },
  { id: "municipal", name: "Municipal", icon: "🏛️", color: "#3B82F6" },
  { id: "religioso", name: "Religioso", icon: "⛪", color: "#EC4899" },
  { id: "escolar", name: "Escolar", icon: "🎓", color: "#06B6D4" },
  { id: "otro", name: "Otro", icon: "📌", color: "#6B7280" },
];

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString("es-HN", {
    weekday: "long",
    day: "numeric",
    month: "long"
  });
};

const formatTime = (timeStr) => {
  if (!timeStr) return "";
  const [hours, minutes] = timeStr.split(":");
  const h = parseInt(hours);
  const ampm = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

export default function EventosPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchEvents();
  }, [category]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (category !== "all") params.append("category", category);
      params.append("upcoming", "true");
      params.append("limit", "50");
      
      const response = await axios.get(`${API}/public/events?${params}`);
      setEvents(response.data.items || []);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryInfo = (catId) => {
    return CATEGORIES.find(c => c.id === catId) || CATEGORIES[CATEGORIES.length - 1];
  };

  const filteredEvents = events.filter(event => {
    if (!search) return true;
    const s = search.toLowerCase();
    return event.title?.toLowerCase().includes(s) ||
           event.description?.toLowerCase().includes(s) ||
           event.location?.toLowerCase().includes(s);
  });

  // Group events by date
  const groupedEvents = filteredEvents.reduce((acc, event) => {
    const date = event.start_date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(event);
    return acc;
  }, {});

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        {/* Hero */}
        <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white py-12 px-4">
          <div className="max-w-6xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold mb-3">
              Agenda de Eventos
            </h1>
            <p className="text-emerald-100 text-lg">
              Ferias, conciertos, actividades y más en Choluteca
            </p>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar eventos..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="event-search"
              />
            </div>

            {/* Category Pills */}
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setCategory(cat.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    category === cat.id
                      ? "text-white shadow-md"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                  style={category === cat.id ? { backgroundColor: cat.color } : {}}
                  data-testid={`category-${cat.id}`}
                >
                  <span className="mr-1">{cat.icon}</span>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {/* Events List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
              <p className="text-gray-500 mt-4">Cargando eventos...</p>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border">
              <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay eventos próximos</h3>
              <p className="text-gray-500">
                {category !== "all" ? "Prueba con otra categoría" : "Vuelve pronto para ver nuevos eventos"}
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {Object.entries(groupedEvents).sort().map(([date, dayEvents]) => (
                <div key={date}>
                  {/* Date Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-center min-w-[80px]">
                      <p className="text-2xl font-bold">{new Date(date + "T00:00:00").getDate()}</p>
                      <p className="text-xs uppercase">
                        {new Date(date + "T00:00:00").toLocaleDateString("es-HN", { month: "short" })}
                      </p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 capitalize">
                        {formatDate(date)}
                      </p>
                      <p className="text-sm text-gray-500">{dayEvents.length} evento(s)</p>
                    </div>
                  </div>

                  {/* Events for this date */}
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {dayEvents.map(event => {
                      const catInfo = getCategoryInfo(event.category);
                      return (
                        <Link
                          key={event.event_id}
                          to={`/eventos/${event.slug}`}
                          className="bg-white rounded-xl border overflow-hidden hover:shadow-lg transition-shadow group"
                          data-testid={`event-card-${event.event_id}`}
                        >
                          {/* Image */}
                          <div 
                            className="h-40 bg-gradient-to-br from-gray-100 to-gray-200 relative"
                            style={event.image_url ? {
                              backgroundImage: `url(${event.image_url})`,
                              backgroundSize: "cover",
                              backgroundPosition: "center"
                            } : {}}
                          >
                            <Badge 
                              className="absolute top-3 left-3 text-white"
                              style={{ backgroundColor: catInfo.color }}
                            >
                              {catInfo.icon} {catInfo.name}
                            </Badge>
                            {event.is_featured && (
                              <Badge className="absolute top-3 right-3 bg-amber-500 text-white">
                                Destacado
                              </Badge>
                            )}
                          </div>

                          {/* Content */}
                          <div className="p-4">
                            <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors line-clamp-2">
                              {event.title}
                            </h3>

                            {event.start_time && (
                              <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                                <Clock className="h-4 w-4" />
                                {formatTime(event.start_time)}
                                {event.end_time && ` - ${formatTime(event.end_time)}`}
                              </div>
                            )}

                            {event.location && (
                              <div className="flex items-center gap-2 text-sm text-gray-500">
                                <MapPin className="h-4 w-4" />
                                <span className="truncate">{event.location}</span>
                              </div>
                            )}

                            <div className="flex items-center justify-between mt-3 pt-3 border-t">
                              <span className="text-xs text-gray-400">
                                {event.view_count || 0} vistas
                              </span>
                              <span className="text-emerald-600 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                                Ver más <ChevronRight className="h-4 w-4" />
                              </span>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
